import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../../utils/colors';
import { usePortalAuth } from '../../context/portalAuthContext';
import portalDataService from '../../services/portalDataService';

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: UI_COLORS.TEXT.MUTED }}>{label}</div>
      <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{value}</div>
    </div>
  );
};

const PortalProfilePage = () => {
  const { t } = useTranslation();
  const s = t('portal.profile', { returnObjects: true });
  const { customer, refreshCustomer } = usePortalAuth();

  const [locations, setLocations] = useState([]);
  const [locLoaded, setLocLoaded] = useState(false);

  // Change-password form
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwNotice, setPwNotice] = useState('');

  useEffect(() => { refreshCustomer(); }, [refreshCustomer]);

  const loadLocations = useCallback(async () => {
    const res = await portalDataService.getMyLocations();
    if (res.success) setLocations(res.data || []);
    setLocLoaded(true);
  }, []);
  useEffect(() => { loadLocations(); }, [loadLocations]);

  const address = [customer?.address_1, customer?.address_2, customer?.city, customer?.state, customer?.zip_code, customer?.country].filter(Boolean).join(', ');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwBusy) return;
    setPwError(''); setPwNotice('');
    if (!current || !next) { setPwError(s?.password?.errors?.required || 'Enter your current and new password.'); return; }
    if (next.length < 8) { setPwError(s?.password?.errors?.short || 'New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setPwError(s?.password?.errors?.mismatch || 'New passwords do not match.'); return; }
    setPwBusy(true);
    const res = await portalDataService.changePassword(current, next);
    setPwBusy(false);
    if (res.success) {
      setPwNotice(s?.password?.success || 'Password updated.');
      setCurrent(''); setNext(''); setConfirm('');
    } else {
      setPwError(res.error || (s?.password?.errors?.failed || 'Failed to update password.'));
    }
  };

  const inputStyle = { backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{s?.title || 'My Profile'}</h1>

      {/* Read-only profile */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-wide mb-2" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{s?.details || 'Details'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label={s?.fields?.company || 'Company'} value={customer?.company_name} />
          <Field label={s?.fields?.owner || 'Owner'} value={customer?.owner_name} />
          <Field label={s?.fields?.contact || 'Contact person'} value={customer?.contact_person} />
          <Field label={s?.fields?.type || 'Type'} value={customer?.customer_type} />
          <Field label={s?.fields?.email || 'Email'} value={customer?.email} />
          <Field label={s?.fields?.phone1 || 'Phone'} value={customer?.phone_number_1} />
          <Field label={s?.fields?.phone2 || 'Alternate phone'} value={customer?.phone_number_2} />
          <Field label={s?.fields?.gst || 'GST number'} value={customer?.gst_number} />
          <Field label={s?.fields?.address || 'Address'} value={address} />
          <Field label={s?.fields?.deliveryLocation || 'Delivery location'} value={customer?.delivery_location} />
        </div>
      </section>

      {/* Saved delivery locations */}
      {locLoaded && locations.length > 0 && (
        <section>
          <h2 className="text-sm font-bold uppercase tracking-wide mb-2" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{s?.savedLocations || 'Saved delivery locations'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {locations.map((l) => (
              <div key={l.id} className="rounded-xl border p-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{l.name || l.delivery_location || `#${l.id}`}</span>
                  {l.is_default && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.12) }}>{s?.default || 'Default'}</span>}
                </div>
                <div className="text-xs mt-0.5" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {[l.delivery_location, l.city, l.state].filter(Boolean).join(', ') || '—'}
                </div>
                {(l.contact_person || l.contact_phone) && (
                  <div className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{[l.contact_person, l.contact_phone].filter(Boolean).join(' · ')}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Change password */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-wide mb-2" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{s?.password?.title || 'Change password'}</h2>
        <form onSubmit={handleChangePassword} className="max-w-md rounded-xl border p-4 space-y-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.password?.current || 'Current password'}</label>
            <input type="password" value={current} onChange={(e) => { setCurrent(e.target.value); if (pwError) setPwError(''); }} autoComplete="current-password" className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.password?.new || 'New password'} ({s?.password?.min8 || 'min 8 chars'})</label>
            <input type="password" value={next} onChange={(e) => { setNext(e.target.value); if (pwError) setPwError(''); }} autoComplete="new-password" className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.password?.confirm || 'Confirm new password'}</label>
            <input type="password" value={confirm} onChange={(e) => { setConfirm(e.target.value); if (pwError) setPwError(''); }} autoComplete="new-password" className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          {pwError && <div className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{pwError}</div>}
          {pwNotice && <div className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{pwNotice}</div>}
          <button type="submit" disabled={pwBusy} className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60" style={{ background: GRADIENTS.PRIMARY, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
            {pwBusy ? (s?.password?.saving || 'Updating…') : (s?.password?.submit || 'Update password')}
          </button>
        </form>
      </section>
    </div>
  );
};

export default PortalProfilePage;
